import React from "react";
import PropTypes from "prop-types";
import Text from "@gc-npm/components.text";
import Price from "@gc-npm/components.price";
import Image from "@gc-npm/components.c-image";
import IconGlyph from "@gc-npm/components.icon-glyph";
import Button from "@gc-npm/components.button";
import Label from "@gc-npm/components.cms-block.cms-label";

const ElementFactory = ({ elementKey, element, activeBreakpoint, isEditingCurrentScene }) => {
  // 👁️ CONTROL DE VISIBILIDAD: Si viene la propiedad y está fijada en false para este breakpoint, se oculta.
  if (element.visibility && element.visibility[activeBreakpoint] === false) {
    return null;
  }

  // 🌟 VOLVEMOS AL ORIGEN: Las posiciones solo manejan top, left, right, bottom, transform y zIndex
  const baseElementStyle = {
    position: "absolute",
    ...element.positions?.[activeBreakpoint],
  };
  
  // Recuperamos la lectura de tamaños exacta del primer archivo
  const currentSize = element.sizes
    ? element.sizes[activeBreakpoint] || { height: "auto", width: "auto" }
    : { height: "auto", width: "auto" };

  let componentToRender = null;

  // 📝 A. Rama de Componentes de Texto
  if (elementKey.includes("Text-")) {
    componentToRender = (
      <Text
        {...element}
        text={element.text}
        color={element.color}
        fontType={element.fontType}
        size={element.size}
        align={element.align}
      />
    );
  // 💰 B. Rama de Componentes de Precio
  } else if (elementKey.includes("Price-")) {
    componentToRender = (
      <Price
        {...element}
        price={element.amount}
        currency={element.unity}
        quote={element.frequency}
      />
    );
  // 🖼️ C. Rama de Componentes de Imagen (Réplica exacta de tu primer archivo)
  } else if (elementKey.includes("Image-")) {
    const radiusVal = element.borderRadius || "0px";

    componentToRender = (
      <div
        style={{
          width: currentSize.width,
          height: currentSize.height,
          borderRadius: radiusVal,
          overflow: "hidden", 
          position: "relative",
          display: "block",
        }}
      >
        <Image
          {...element}
          style={{
            width: "100%",
            height: "100%",
            objectFit: element.objectFit || "cover",
          }}
          width={currentSize.width}
          height={currentSize.height}
        />
      </div>
    );
  // 😎 D. Rama de Iconos Vectoriales (Glyphs)
  } else if (elementKey.includes("Glyph-")) {
    componentToRender = (
      <IconGlyph
        id={element.glyphId}
        height={currentSize.height}
        layoutSize={{
          width: currentSize.width,
          height: currentSize.height,
        }}
        color={element.color}
      />
    );
  // 🕹️ E. Rama de Botones / CTAs
  } else if (elementKey.includes("Button-")) {
    componentToRender = (
      <div
        style={{
          pointerEvents: isEditingCurrentScene ? "none" : "auto",
          display: "contents", 
          borderRadius: element.borderRadius || "0px",
        }}
      >
        <Button {...element} />
      </div>
    );
  // 🏷️ F. Rama de Etiquetas / Badges
  } else if (elementKey.includes("Label-")) {
    componentToRender = <Label {...element} />;
  }

  return (
    <div
      id={elementKey}
      data-id={elementKey}
      draggable={isEditingCurrentScene}
      style={{
        ...baseElementStyle,
        zIndex: undefined, // Se limpia aquí para delegarlo estrictamente al contenedor SceneLayer padre
        pointerEvents: isEditingCurrentScene ? "auto" : "none",
        cursor: isEditingCurrentScene ? "move" : "default",
      }}
    >
      {componentToRender}
    </div>
  );
};

ElementFactory.propTypes = {
  elementKey: PropTypes.string.isRequired,
  element: PropTypes.object.isRequired,
  activeBreakpoint: PropTypes.string.isRequired,
  isEditingCurrentScene: PropTypes.bool.isRequired,
};

export default ElementFactory;
